import { Toolbar } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ToResolveClassNameError from "../../components/hoc/ToResolveClassNameError";
import JobListSubheader from "../../components/jobs/JobListSubheader/JobListSubheader";
import JobListWrapper from "../../components/jobs/JobListWrapper/JobListWrapper";
import SharedCollapsibleFooter from "../../components/shared/SharedCollapsibleFooter/SharedCollapsibleFooter";
import SharedJobListHeader from "../../components/shared/SharedHeader/SharedJobListHeader/ShareJobListHeader";
import { formJobQueryParam } from "../../functions/job";
import {
  check404,
  convertUrlToIds,
  encodePage,
  getSelectedTab,
  getTitle,
} from "../../helpers/filter_management";
import {
  getCmsLocations,
  getCmsSpecialisations,
  getFooterCompanyWriteup,
  getJobsWithoutRedux,
  getSpecialisationWithoutRedux,
  getStateRegionWithoutRedux,
} from "../../redux/actions/job_action";
import JLPUploadResumeFloating from "../../components/shared/UploadResumeFloating/wrappers/JLPUploadResumeFloating";

function JobFiltersPage(props) {
  const {
    jobs,
    ssrFilterParams,
    ssrPageParams,
    currentTab,
    titleInfo,
    specialisationList,
    cmsSpecialisations,
    cmsLocations,
    companyWriteup,
  } = props;
  const { i18n } = useTranslation();
  const [localizedCompanyWriteup, setLocalizedCompanyWriteup] =
    useState(companyWriteup);

  useEffect(() => {
    setLocalizedCompanyWriteup(companyWriteup);
  }, [companyWriteup]);

  useEffect(() => {
    let isMounted = true;

    const fetchLocalizedFooterWriteup = async () => {
      const language = i18n?.resolvedLanguage || i18n?.language;
      if (!language) return;

      const nextCompanyWriteup = await getFooterCompanyWriteup({
        locale: language,
      });

      if (isMounted && nextCompanyWriteup) {
        setLocalizedCompanyWriteup(nextCompanyWriteup);
      }
    };

    fetchLocalizedFooterWriteup();

    return () => {
      isMounted = false;
    };
  }, [i18n?.language, i18n?.resolvedLanguage]);

  // const router = useRouter();
  // const isLoggedIn = checkAuthentication();

  // useEffect(() => {
  //   if (!router.isReady) return;

  //   const isBaseJobs = router.asPath === "/jobs" || router.asPath === "/jobs/";

  //   if (isBaseJobs && !isLoggedIn) {
  //     router.replace("/");
  //   }
  // }, [router.isReady, router.asPath]);

  return (
    <Fragment>
      <SharedJobListHeader
        jobs={jobs}
        ssrFilterParams={ssrFilterParams}
        specialisationList={specialisationList}
      />

      <ToResolveClassNameError>
        <Toolbar
          sx={{
            // minHeight: "64px",
            minHeight: "104px !important",
            "@media(max-width: 1279px)": {
              // minHeight: "52px",
              minHeight: "92px !important",
            },
            "@media(max-width: 640px)": {
              // minHeight: "60px",
              minHeight: "100px !important",
            },
          }}
        />
      </ToResolveClassNameError>
      <JobListSubheader
        titleInfo={titleInfo ?? null}
        filterParams={ssrFilterParams ?? null}
      />
      <JobListWrapper
        serverJobs={jobs}
        ssrFilterParams={ssrFilterParams}
        ssrPageParams={ssrPageParams}
        currentTab={currentTab}
        cmsSpecialisations={cmsSpecialisations}
        cmsLocations={cmsLocations}
        companyWriteup={localizedCompanyWriteup}
      />

      <SharedCollapsibleFooter />
      {/* SSR component END */}
    </Fragment>
  );
}

export async function getServerSideProps(context) {
  const { query, req, locale } = context;
  const detectedLocale =
    locale ||
    req?.cookies?.NEXT_LOCALE ||
    req?.cookies?.i18next ||
    req?.headers?.["x-next-intl-locale"] ||
    "en";
  if (process.env.NODE_ENV !== "production") {
    console.log("[jobs-ssr] context.locale:", locale);
    console.log("[jobs-ssr] detected locale:", detectedLocale);
  }

  // // Check if user is accessing the base /jobs URL without any filters
  // // The catch-all route [...filter] means:
  // // - /jobs -> query.filter is undefined
  // // - /jobs/something -> query.filter is ['something']
  // // - /jobs/something/else -> query.filter is ['something', 'else']
  // const isBaseJobsUrl =
  //   !query?.filter ||
  //   (Array.isArray(query.filter) && query.filter.length === 0);

  // // Check if user is authenticated by looking for JWT token in cookies
  // const isAuthenticated =
  //   req.cookies && req.cookies[process.env.NEXT_PUBLIC_JWT_COOKIE];

  // // If accessing base jobs URL without authentication, redirect to home page
  // if (isBaseJobsUrl && !isAuthenticated) {
  //   return {
  //     redirect: {
  //       destination: "/",
  //       permanent: false,
  //     },
  //   };
  // }

  const pageExist = check404(query?.filter);

  if (pageExist) {
    return {
      notFound: true,
    };
  }

  const specialisationList = await getSpecialisationWithoutRedux();
  const stateList = await getStateRegionWithoutRedux();

  const filterParams = convertUrlToIds(query, specialisationList, stateList);

  const currentPage = encodePage(query?.page);

  const currentTab = getSelectedTab(
    filterParams,
    currentPage,
    req.cookies.defaultFyp
  );

  const jobParams = formJobQueryParam(filterParams, false);

  const pagination = {
    first: 30,
    last: null,
    startCursor: currentPage ?? null,
    endCursor: null,
  };

  const jobs = await getJobsWithoutRedux(jobParams, pagination);
  const titleInfo = getTitle(filterParams, specialisationList);

  let cmsSpecialisations = (await getCmsSpecialisations()) ?? [];
  let cmsLocations = (await getCmsLocations()) ?? [];
  let companyWriteup =
    (await getFooterCompanyWriteup({ locale: detectedLocale })) ?? {};

  return {
    props: {
      currentTab: currentTab,
      jobs: jobs?.jobs ?? [],
      ssrFilterParams: filterParams,
      ssrPageParams: currentPage ?? null,
      titleInfo: titleInfo,
      cmsSpecialisations: cmsSpecialisations ?? [],
      cmsLocations: cmsLocations ?? [],
      companyWriteup: companyWriteup ?? {},
      specialisationList: specialisationList ?? [],
    },
  };
}

export default JobFiltersPage;
